/**
 * Pure event validation functions. No framework dependencies.
 * Can be used in any TypeScript environment (Angular, Node, Dart transpilation target).
 */

import { AnalyticsEvent, EventType, EventData, ContextInfo } from '../models/event-types';
import { extractSku, extractProductStoreCode, extractNearestStoreCode } from '../helpers/analytics-helpers';

export interface ValidationResult {
  isValid: boolean;
  reason?: string;
}

const VALID: ValidationResult = { isValid: true };
const invalid = (reason: string): ValidationResult => ({ isValid: false, reason });

/**
 * Main event validation entry point. Pure function, no side effects.
 */
export function validateEvent(event: AnalyticsEvent): ValidationResult {
  if (!event.userId || typeof event.userId !== 'string' || event.userId.trim() === '') {
    return invalid('Missing or invalid userId');
  }
  if (!event.eventType || typeof event.eventType !== 'string') {
    return invalid('Missing or invalid eventType');
  }
  if (!event.eventData || typeof event.eventData !== 'object') {
    return invalid('Missing or invalid eventData');
  }

  return validateEventData(event.eventType, event.eventData, event.contextInfo);
}

/**
 * Validate event data based on event type.
 */
export function validateEventData(
  eventType: EventType,
  eventData: EventData,
  contextInfo?: ContextInfo,
): ValidationResult {
  switch (eventType) {
    // Product events
    case 'product_view':
      return validateProductEvent(eventData, 'SKU and storeCode required for product tracking', contextInfo);

    case 'add_to_cart':
    case 'remove_from_cart': {
      const cartValidation = validateProductEvent(eventData, 'SKU and storeCode required for cart operations', contextInfo);
      if (!cartValidation.isValid) return cartValidation;
      return validateQuantity(eventData, 'Quantity required for cart operations');
    }

    case 'product_hover':
    case 'recommendation_click':
    case 'wishlist_add':
    case 'wishlist_remove':
    case 'share':
      return validateProductEvent(eventData, 'SKU and storeCode required for product interactions', contextInfo);

    case 'review_submit': {
      const reviewValidation = validateProductEvent(eventData, 'SKU and storeCode required for reviews', contextInfo);
      if (!reviewValidation.isValid) return reviewValidation;
      return validateReviewData(eventData);
    }

    case 'carousel_interaction':
      return validateCarouselData(eventData);

    case 'search_click': {
      const searchClickValidation = validateSearchQuery(eventData, 'Search query required for search clicks');
      if (!searchClickValidation.isValid) return searchClickValidation;
      return validateProductEvent(eventData, 'SKU and storeCode required for search clicks', contextInfo);
    }

    // Ecommerce events
    case 'checkout_start':
      return validateCheckoutData(eventData);

    case 'checkout_complete': {
      const orderValidation = validateOrderData(eventData);
      if (!orderValidation.isValid) return orderValidation;
      return validateCheckoutData(eventData);
    }

    // Search events
    case 'search':
      return validateSearchQuery(eventData, 'Query and result count required for search tracking');

    // User events
    case 'user_login':
      return validateLoginData(eventData);

    case 'user_logout':
      return VALID;

    // Page events
    case 'page_view':
      return validatePageData(eventData);

    // Category events
    case 'category_browse':
      return validateCategoryData(eventData);

    // Store events
    case 'store_selection':
      return validateStoreSelectionData(eventData);

    // Form events
    case 'form_submit':
      return validateFormData(eventData);

    // Interaction events
    case 'click':
      return validateClickData(eventData);

    case 'scroll':
      return validateScrollData(eventData);

    case 'dwell_time':
      return validateDwellTimeData(eventData);

    case 'mouse_movement':
      return validateMouseMovementData(eventData);

    case 'tab_visibility':
      return validateTabVisibilityData(eventData);

    case 'idle_time':
      return validateIdleTimeData(eventData);

    case 'copy_paste':
      return validateCopyPasteData(eventData);

    case 'error':
      return validateErrorData(eventData);

    // Phase 1 events
    case 'product_impression':
      return validateProductImpressionData(eventData);

    case 'search_impression':
      return validateSearchImpressionData(eventData);

    case 'view_cart':
      return validateViewCartData(eventData);

    case 'add_shipping_info':
      return validateShippingInfoData(eventData);

    case 'add_payment_info':
      return validatePaymentInfoData(eventData);

    case 'notification_received':
    case 'notification_opened':
      return validateNotificationData(eventData);

    case 'order_tracking_view':
      return validateOrderTrackingData(eventData);

    case 'store_follow':
    case 'store_unfollow':
      return validateStoreFollowData(eventData);

    case 'refund_requested':
      return validateRefundData(eventData);

    case 'delivery_rating':
      return validateDeliveryRatingData(eventData);

    case 'promotion_view':
    case 'promotion_click':
      return validatePromotionData(eventData);

    case 'print':
    case 'inquiry':
    case 'report':
      return validateProductEvent(eventData, 'SKU and storeCode required', contextInfo);

    // Phase 4: Seller analytics
    case 'seller_order_view':
      return validateSellerStoreCode(eventData, 'orderId');

    case 'seller_order_status_change':
      return validateSellerOrderStatusChange(eventData);

    case 'seller_product_create':
    case 'seller_product_edit':
    case 'seller_product_toggle':
    case 'seller_product_delete':
      return validateSellerProductEvent(eventData);

    case 'seller_dashboard_view':
    case 'seller_dashboard_filter':
    case 'seller_payout_step':
    case 'seller_geolocation_toggle':
      return validateSellerStoreCode(eventData);

    case 'seller_live_event_start':
    case 'seller_live_event_end':
      return validateSellerStoreCode(eventData);

    case 'seller_inventory_adjust':
      return validateSellerInventoryAdjust(eventData);

    case 'seller_barcode_scan':
      return validateSellerStoreCode(eventData);

    // Phase 5: Event attendance analytics
    case 'seller_event_attendance_declare':
    case 'seller_event_attendance_cancel':
    case 'seller_event_suggestion_accept':
    case 'seller_event_suggestion_dismiss':
      return validateSellerEventAttendance(eventData);

    default:
      return invalid(`Unknown event type: ${eventType}`);
  }
}

// ====================================================================
// Specific validators
// ====================================================================

function validateProductEvent(eventData: EventData, message: string, contextInfo?: ContextInfo): ValidationResult {
  const sku = extractSku(eventData as unknown as Record<string, unknown>);
  if (!sku || sku.trim() === '') {
    return invalid(`Missing SKU - ${message}`);
  }
  const productStoreCode = extractProductStoreCode(eventData as unknown as Record<string, unknown>);
  if (!productStoreCode || productStoreCode.trim() === '') {
    return invalid(`Missing product storeCode in eventData - ${message}`);
  }
  const nearestStoreCode = extractNearestStoreCode(contextInfo);
  if (!nearestStoreCode || nearestStoreCode.trim() === '') {
    return invalid(`Missing storeCode in contextInfo - ${message}`);
  }
  return VALID;
}

function validateQuantity(eventData: EventData, message: string): ValidationResult {
  if ('quantity' in eventData && typeof eventData.quantity === 'number' && eventData.quantity > 0) {
    return VALID;
  }
  return invalid(message);
}

function validateReviewData(eventData: EventData): ValidationResult {
  if ('rating' in eventData && typeof eventData.rating === 'number' && eventData.rating >= 1 && eventData.rating <= 5) {
    return VALID;
  }
  return invalid('Rating required for review submission');
}

function validateSearchQuery(eventData: EventData, message: string): ValidationResult {
  if ('query' in eventData && typeof eventData.query === 'string' && eventData.query.trim() !== '') {
    return VALID;
  }
  return invalid(message);
}

function validateCheckoutData(eventData: EventData): ValidationResult {
  if (
    'cartValue' in eventData && typeof eventData.cartValue === 'number' && eventData.cartValue >= 0 &&
    'itemCount' in eventData && typeof eventData.itemCount === 'number' && eventData.itemCount > 0
  ) {
    return VALID;
  }
  return invalid('Cart value and item count required for checkout');
}

function validateOrderData(eventData: EventData): ValidationResult {
  if (
    'orderId' in eventData && typeof eventData.orderId === 'string' && eventData.orderId.trim() !== '' &&
    'orderValue' in eventData && typeof eventData.orderValue === 'number' && eventData.orderValue >= 0
  ) {
    return VALID;
  }
  return invalid('Order details required for checkout completion');
}

function validateLoginData(eventData: EventData): ValidationResult {
  if ('method' in eventData && typeof eventData.method === 'string' && eventData.method.trim() !== '') {
    return VALID;
  }
  return invalid('Login method required');
}

function validatePageData(eventData: EventData): ValidationResult {
  if ('page' in eventData && typeof eventData.page === 'string' && eventData.page.trim() !== '') {
    return VALID;
  }
  return invalid('Page information required for page view tracking');
}

function validateCategoryData(eventData: EventData): ValidationResult {
  if ('categoryId' in eventData && typeof eventData.categoryId === 'string' && eventData.categoryId.trim() !== '') {
    return VALID;
  }
  return invalid('Category ID required');
}

function validateStoreSelectionData(eventData: EventData): ValidationResult {
  if (
    'storeCode' in eventData && typeof eventData.storeCode === 'string' && eventData.storeCode.trim() !== '' &&
    'selectionMethod' in eventData && typeof eventData.selectionMethod === 'string'
  ) {
    return VALID;
  }
  return invalid('Store code and selection method required');
}

function validateFormData(eventData: EventData): ValidationResult {
  if (
    'formName' in eventData && typeof eventData.formName === 'string' && eventData.formName.trim() !== '' &&
    'fieldCount' in eventData && typeof eventData.fieldCount === 'number' && eventData.fieldCount > 0
  ) {
    return VALID;
  }
  return invalid('Form name and field count required');
}

function validateClickData(eventData: EventData): ValidationResult {
  if ('element' in eventData && typeof eventData.element === 'string' && eventData.element.trim() !== '') {
    return VALID;
  }
  return invalid('Element information required for click tracking');
}

function validateCarouselData(eventData: EventData): ValidationResult {
  if (
    'action' in eventData && typeof eventData.action === 'string' && eventData.action.trim() !== '' &&
    'position' in eventData && typeof eventData.position === 'number'
  ) {
    return VALID;
  }
  return invalid('Carousel interaction requires action and position');
}

function validateScrollData(eventData: EventData): ValidationResult {
  if (
    'scrollDepthPercent' in eventData && typeof eventData.scrollDepthPercent === 'number' &&
    'scrollSpeed' in eventData && typeof eventData.scrollSpeed === 'number' &&
    'direction' in eventData && typeof eventData.direction === 'string'
  ) {
    return VALID;
  }
  return invalid('Scroll information required');
}

function validateDwellTimeData(eventData: EventData): ValidationResult {
  if ('durationMs' in eventData && typeof eventData.durationMs === 'number' && eventData.durationMs >= 0) {
    return VALID;
  }
  return invalid('Duration required for dwell time tracking');
}

function validateMouseMovementData(eventData: EventData): ValidationResult {
  if (
    'density' in eventData && typeof eventData.density === 'number' &&
    'coverage' in eventData && typeof eventData.coverage === 'number'
  ) {
    return VALID;
  }
  return invalid('Movement data required');
}

function validateTabVisibilityData(eventData: EventData): ValidationResult {
  if (
    'state' in eventData && typeof eventData.state === 'string' &&
    (eventData.state === 'hidden' || eventData.state === 'visible')
  ) {
    return VALID;
  }
  return invalid('Visibility state required');
}

function validateIdleTimeData(eventData: EventData): ValidationResult {
  if (
    'idleDurationMs' in eventData && typeof eventData.idleDurationMs === 'number' && eventData.idleDurationMs >= 0 &&
    'triggerEvent' in eventData && typeof eventData.triggerEvent === 'string'
  ) {
    return VALID;
  }
  return invalid('Idle duration required');
}

function validateCopyPasteData(eventData: EventData): ValidationResult {
  if ('copiedTextLength' in eventData && typeof eventData.copiedTextLength === 'number' && eventData.copiedTextLength >= 0) {
    return VALID;
  }
  return invalid('Copy/paste information required');
}

function validateErrorData(eventData: EventData): ValidationResult {
  if ('errorMessage' in eventData && typeof eventData.errorMessage === 'string' && eventData.errorMessage.trim() !== '') {
    return VALID;
  }
  return invalid('Error message required');
}

// Phase 1 validators

function validateProductImpressionData(eventData: EventData): ValidationResult {
  if (
    'skus' in eventData && Array.isArray(eventData.skus) && eventData.skus.length > 0 &&
    'source' in eventData && typeof eventData.source === 'string' &&
    'positionStart' in eventData && typeof eventData.positionStart === 'number' &&
    'positionEnd' in eventData && typeof eventData.positionEnd === 'number'
  ) {
    return VALID;
  }
  return invalid('SKUs and source required for product impression tracking');
}

function validateSearchImpressionData(eventData: EventData): ValidationResult {
  if (
    'query' in eventData && typeof eventData.query === 'string' && eventData.query.trim() !== '' &&
    'results' in eventData && Array.isArray(eventData.results) &&
    'resultCount' in eventData && typeof eventData.resultCount === 'number'
  ) {
    return VALID;
  }
  return invalid('Query and results required for search impression tracking');
}

function validateViewCartData(eventData: EventData): ValidationResult {
  if (
    'itemCount' in eventData && typeof eventData.itemCount === 'number' && eventData.itemCount >= 0 &&
    'cartTotal' in eventData && typeof eventData.cartTotal === 'number' && eventData.cartTotal >= 0 &&
    'items' in eventData && Array.isArray(eventData.items)
  ) {
    return VALID;
  }
  return invalid('Item count, cart total, and items required for view cart tracking');
}

function validateShippingInfoData(eventData: EventData): ValidationResult {
  if (
    'shippingMethod' in eventData && typeof eventData.shippingMethod === 'string' && eventData.shippingMethod.trim() !== '' &&
    'cartTotal' in eventData && typeof eventData.cartTotal === 'number' && eventData.cartTotal >= 0
  ) {
    return VALID;
  }
  return invalid('Shipping method and cart total required');
}

function validatePaymentInfoData(eventData: EventData): ValidationResult {
  if (
    'paymentMethod' in eventData && typeof eventData.paymentMethod === 'string' && eventData.paymentMethod.trim() !== '' &&
    'cartTotal' in eventData && typeof eventData.cartTotal === 'number' && eventData.cartTotal >= 0
  ) {
    return VALID;
  }
  return invalid('Payment method and cart total required');
}

function validateNotificationData(eventData: EventData): ValidationResult {
  if (
    'notificationId' in eventData && typeof eventData.notificationId === 'string' && eventData.notificationId.trim() !== '' &&
    'notificationType' in eventData && typeof eventData.notificationType === 'string' && eventData.notificationType.trim() !== ''
  ) {
    return VALID;
  }
  return invalid('Notification ID and type required');
}

function validateOrderTrackingData(eventData: EventData): ValidationResult {
  if (
    'orderId' in eventData && typeof eventData.orderId === 'string' && eventData.orderId.trim() !== '' &&
    'orderStatus' in eventData && typeof eventData.orderStatus === 'string' && eventData.orderStatus.trim() !== ''
  ) {
    return VALID;
  }
  return invalid('Order ID and status required for order tracking view');
}

function validateStoreFollowData(eventData: EventData): ValidationResult {
  if ('storeCode' in eventData && typeof eventData.storeCode === 'string' && eventData.storeCode.trim() !== '') {
    return VALID;
  }
  return invalid('Store code required');
}

function validateRefundData(eventData: EventData): ValidationResult {
  if ('orderId' in eventData && typeof eventData.orderId === 'string' && eventData.orderId.trim() !== '') {
    return VALID;
  }
  return invalid('Order ID required for refund request tracking');
}

function validateDeliveryRatingData(eventData: EventData): ValidationResult {
  if (
    'orderId' in eventData && typeof eventData.orderId === 'string' && eventData.orderId.trim() !== '' &&
    'rating' in eventData && typeof eventData.rating === 'number' && eventData.rating >= 1 && eventData.rating <= 5
  ) {
    return VALID;
  }
  return invalid('Order ID and rating required for delivery rating tracking');
}

function validatePromotionData(eventData: EventData): ValidationResult {
  if ('promotionId' in eventData && typeof eventData.promotionId === 'string' && eventData.promotionId.trim() !== '') {
    return VALID;
  }
  return invalid('Promotion ID required');
}

// Phase 4: Seller analytics validators

function validateSellerStoreCode(eventData: EventData, additionalField?: string): ValidationResult {
  if (!('storeCode' in eventData) || typeof eventData.storeCode !== 'string' || eventData.storeCode.trim() === '') {
    return invalid('Store code required for seller events');
  }
  if (additionalField && additionalField === 'orderId') {
    if (!('orderId' in eventData) || typeof eventData.orderId !== 'string' || eventData.orderId.trim() === '') {
      return invalid('Order ID required for seller order events');
    }
  }
  return VALID;
}

function validateSellerOrderStatusChange(eventData: EventData): ValidationResult {
  const storeCheck = validateSellerStoreCode(eventData);
  if (!storeCheck.isValid) return storeCheck;
  if (
    'orderId' in eventData && typeof eventData.orderId === 'string' && eventData.orderId.trim() !== '' &&
    'previousStatus' in eventData && typeof eventData.previousStatus === 'string' && eventData.previousStatus.trim() !== '' &&
    'newStatus' in eventData && typeof eventData.newStatus === 'string' && eventData.newStatus.trim() !== ''
  ) {
    return VALID;
  }
  return invalid('Order ID and status transition required');
}

function validateSellerProductEvent(eventData: EventData): ValidationResult {
  if (
    'sku' in eventData && typeof eventData.sku === 'string' && eventData.sku.trim() !== '' &&
    'storeCode' in eventData && typeof eventData.storeCode === 'string' && eventData.storeCode.trim() !== ''
  ) {
    return VALID;
  }
  return invalid('SKU and store code required for seller product events');
}

function validateSellerInventoryAdjust(eventData: EventData): ValidationResult {
  const productCheck = validateSellerProductEvent(eventData);
  if (!productCheck.isValid) return productCheck;
  if (
    'previousQuantity' in eventData && typeof eventData.previousQuantity === 'number' &&
    'newQuantity' in eventData && typeof eventData.newQuantity === 'number'
  ) {
    return VALID;
  }
  return invalid('Previous and new quantity required for inventory adjustment');
}

// Phase 5: Event attendance validators

function validateSellerEventAttendance(eventData: EventData): ValidationResult {
  if (
    'storeCode' in eventData && typeof eventData.storeCode === 'string' && eventData.storeCode.trim() !== '' &&
    'eventId' in eventData && typeof eventData.eventId === 'string' && eventData.eventId.trim() !== ''
  ) {
    return VALID;
  }
  return invalid('Store code and event ID required for event attendance tracking');
}
